import * as React from 'react';
import withStyles from 'react-jss';
import { connect } from 'react-redux';
import Form from 'antd/lib/form';
import { sortBy } from 'lodash';
import {
  SearchResult,
} from './TypesInDiscoverRates/SearchResult';
import IntersectChargeCard from './IntersectChargeCard';
import { bindActionCreators } from 'redux';
import {
  getAllDataResetFilter,
} from 'src/actions/discoverRatesActions';
import {
  DISCOVER_SELL_RATES,
  DISCOVER_BUY_RATES,
  DISCOVER_RATES,
} from 'src/utils/partsToShow/partsConstants';
import withAuthAccessExim from 'src/components/hoc/withAuthAccessExim';
import { StylesProps } from 'src/Library/Types';
import { Line } from 'react-chartjs-2';
import { AnalyticsViewStyles, PRICE_TREND_COLORS } from './AnalyticsView.styles';
import { tokens } from 'src/design-system';

interface IProps extends StylesProps<ReturnType<typeof AnalyticsViewStyles>> {
  priceTrendsData: any[];
  fetchedResultWithFilters: SearchResult[];
  isLocalsPresent: boolean;
  activeKey: string;
  currencySymbols: any;
  handleNewInquiry: (disableFields: boolean, sendInquiry?: boolean) => void;
  searchResultsWithVendorRatingFilter: any[];
  selectedVendorRating: string[];
}

const AnalyticsView = (props: IProps): React.ReactElement => {
  const { 
     classes,
     priceTrendsData,
     fetchedResultWithFilters,
     isLocalsPresent,
     activeKey,
     currencySymbols,
     handleNewInquiry,
     selectedVendorRating,
     searchResultsWithVendorRatingFilter
    } = props;

  const getCardList = () => {
    if (selectedVendorRating?.length) {
      return searchResultsWithVendorRatingFilter
    } 
    return fetchedResultWithFilters;
  }

  const renderAnalyticsHeader = (title: string): React.ReactElement => {
    return (
      <div style={{ fontSize: tokens.fontSize.body, marginBottom: '10px', fontWeight: tokens.fontWeight.semibold }}>
        <span>{title}</span>
      </div>
    )
  }

  const renderMinimumPriceRate = (): React.ReactElement => {
    const topResult = sortBy(getCardList().filter(rate=>rate?.rank), 'rank')?.[0];
    return (
          topResult &&
          <div data-testid={'minimumPriceRate'}>
            {renderAnalyticsHeader('Minimum Price')}
            <IntersectChargeCard
            domSelector="intersectDiv"
            searchResult={topResult}
            topResult={topResult}
            isItemPresent={{ isLocalsPresent, activeKey, ffListMap: {}, handleNewInquiry, history }}
          />
          </div>
    );
  }

  const getPriceTrendData = () => {
    if (!priceTrendsData) {
      return [];
    }
    const labelMonths = priceTrendsData.map((trendData) => trendData.month);
    const minimumPriceData = priceTrendsData.map((trendData) => trendData.minimumPrice);
    const maximumPriceData = priceTrendsData.map((trendData) => trendData.maximumPrice);
    const averagePriceData = priceTrendsData.map((trendData) => trendData.averagePrice);
    return {
      labels: labelMonths,
      datasets: [
        {
          label: 'Minimum Price',
          data: minimumPriceData,
          borderWidth: 2,
          tension: 0,
          fill: false,
          pointRadius: 0,
          borderColor: PRICE_TREND_COLORS.MINIMUM_PRICE,
        },
        {
          label: 'Maximum Price',
          data: maximumPriceData,
          borderWidth: 2,
          tension: 0,
          fill: false,
          pointRadius: 0,
          borderColor: PRICE_TREND_COLORS.MAXIMUM_PRICE,
        },
        {
          label: 'Average Price',
          data: averagePriceData,
          borderWidth: 2,
          tension: 0,
          fill: false,
          pointRadius: 0,
          borderColor: PRICE_TREND_COLORS.AVERAGE_PRICE,
        },
      ],
    };
  };

  const renderLegend = (label, color): React.ReactElement => {
    return (
      <>
        <div
          style={{
            backgroundColor: color,
            width: '10px',
            height: '10px',
            margin: '0 5px 0 15px',
          }}
        ></div>
        <span>{label}</span>
      </>
    );
  };

  const renderPriceTrendsLegend = (): React.ReactElement => {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {renderLegend('Minimum Price', PRICE_TREND_COLORS.MINIMUM_PRICE)}
        {renderLegend('Maximum Price', PRICE_TREND_COLORS.MAXIMUM_PRICE)}
        {renderLegend('Average Price', PRICE_TREND_COLORS.AVERAGE_PRICE)}
      </div>
    );
  };

  const renderPriceTrendsHeader = (): React.ReactElement => {
    return (
      <div className={classes.priceTrendsHeaderDiv} data-testid={'priceTrendsData'}>
        <span className={classes.priceTrendsHeading}>Price Trends</span>
        <div>{renderPriceTrendsLegend()}</div>
      </div>
    );
  };

  const renderLastBooked = (): React.ReactElement => {
    const topResult = sortBy(getCardList().filter(rate=>rate.lastBookedAt), 'lastBookedAt')?.reverse()?.[0];
    return (
          topResult &&
          <div data-testid={'lastBookedRate'}>
            {renderAnalyticsHeader('Last Booked')}
            <IntersectChargeCard
            domSelector="intersectDiv"
            searchResult={topResult}
            topResult={topResult}
            isItemPresent={{ isLocalsPresent, activeKey, ffListMap: {}, handleNewInquiry, history }}
          />
          </div>
    );
  }

  const renderFastestTransit = (): React.ReactElement => {
    const topResult = sortBy(getCardList().filter(rate=>rate.transitDays), 'transitDays')?.[0];
    return (
          topResult &&
          <div data-testid={'fastestTransitRate'}>
            {renderAnalyticsHeader('Fastest Transit')}
            <IntersectChargeCard
            domSelector="intersectDiv"
            searchResult={topResult}
            topResult={topResult}
            isItemPresent={{ isLocalsPresent, activeKey, ffListMap: {}, handleNewInquiry, history }}
          />
          </div>
    );
  }

  const renderPriceTrendsData = () => {
    const priceTrendData = getPriceTrendData();
    const currencySymbol = currencySymbols?.[priceTrendsData?.[0]?.currency] || priceTrendsData?.[0]?.currency || '';
    const options = {
      legend: {
        display: false,
      },
      scales: {
        yAxes: [
          {
            gridLines: {
              display: false,
            },
            ticks: {
                stepSize: 100,
                callback: function(value, index, values) {
                  return currencySymbol + ' ' + value;
                }
            }
          },
        ],
      },
    };
    return (
      priceTrendsData.length > 0 ?
      <div style={{ width: '96%' }}>
        {renderPriceTrendsHeader()}
        <div style={{ padding: '20px', backgroundColor: tokens.colors.bgWhite }}>
          <Line data={priceTrendData} options={options} redraw={false} />
        </div>
      </div> :
      null
    );
  };

  return <div data-testid={'analyticsView'}>
    {renderMinimumPriceRate()}
    {renderLastBooked()}
    {renderFastestTransit()}
    {renderPriceTrendsData()}
    </div>;
};

const mapStateToProps = ({ masterData, discoverRatesReducer, authUser }, ownProps) => ({
  currencySymbols: masterData.currencySymbols,
  priceTrendsData: discoverRatesReducer.priceTrendsData,
  fetchedResultWithFilters: discoverRatesReducer.fetchedResultWithFilters,
});

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      getAllDataResetFilter,
    },
    dispatch
  );
};

const AnalyticsViewStyled = withStyles(AnalyticsViewStyles)(Form.create()(AnalyticsView));
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  withAuthAccessExim([DISCOVER_SELL_RATES, DISCOVER_BUY_RATES, DISCOVER_RATES])(AnalyticsViewStyled)
);
